/**
 * Shared SEO helpers.
 *
 * Centralises head-tag management (title, description, canonical, Open Graph,
 * Twitter) and the site's structured-data entity graph so every route emits
 * consistent, non-duplicated metadata.
 *
 * IMPORTANT: every value below must be backed by information that already
 * appears on the website. Never add invented addresses, hours, ratings,
 * reviews, awards, or prices here.
 */
export const SITE_URL = 'https://www.fulldetailpro.com';
export const BUSINESS_ID = `${SITE_URL}/#business`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const BUSINESS_NAME = "Mike's Pro Detail";
export const BUSINESS_PHONE = '+18183819905';
export const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/8ujQRT1VS2FomWSs5Jvzhk/mikesprodetail2026logo.png';
/**
 * Cities with dedicated service-area pages, in priority order. Camarillo leads
 * because it is the primary local market.
 */
export const SERVICE_AREA_CITIES = ['Camarillo', 'Ventura', 'Thousand Oaks', 'Simi Valley', 'Moorpark', 'Newbury Park', 'Westlake Village', 'Oak Park', 'Agoura Hills'];
/** Canonical path for a city landing page. */
export function cityPath(slug: string): string {
  // Thousand Oaks has a dedicated keyword URL; every other city uses the
  // /service-areas/<city> pattern.
  if (slug === 'thousand-oaks') return '/mobile-detailing-thousand-oaks';
  return `/service-areas/${slug}`;
}
/** Services that genuinely exist on the site, each with its own page. */
export const SERVICE_CATALOG = [{
  name: 'Mobile Car Detailing',
  path: '/services/mobile-detailing',
  description: 'Full-service mobile auto detailing performed on-site at your home or office.'
}, {
  name: 'Interior Detailing',
  path: '/services/interior-detailing',
  description: 'Deep clean, shampoo, and steam sanitize every surface inside your vehicle.'
}, {
  name: 'Exterior Detailing',
  path: '/services/exterior-detailing',
  description: 'Hand wash, clay bar treatment, and paint protection for a showroom-quality finish.'
}, {
  name: 'Full Detail',
  path: '/full-detail',
  description: 'Complete interior and exterior restoration with hybrid ceramic spray protection.'
}, {
  name: 'Wash & Wax',
  path: '/wash-wax',
  description: 'Exterior wash, vacuum, windows, tire dressing, and a hybrid ceramic spray.'
}, {
  name: 'Basic Wash',
  path: '/basic-wash',
  description: 'Maintenance wash covering the essentials inside and out. Two car minimum.'
}, {
  name: 'Hybrid Ceramic Spray Protection',
  path: '/services/hybrid-ceramic-spray',
  description: 'Sprayed-on hybrid ceramic protection that adds gloss and water beading to your paint.'
}, {
  name: 'Headlight Restoration',
  path: '/services/headlight-restoration',
  description: 'Restore cloudy, yellowed headlights to clear condition.'
}, {
  name: 'Engine Bay Detailing',
  path: '/services/engine-bay-detailing',
  description: 'Safe degreasing and dressing of the engine bay.'
}];
/**
 * The single canonical business entity for the whole site. Every other schema
 * node references it by @id instead of re-declaring a competing business.
 */
export const businessEntity = {
  '@type': ['LocalBusiness', 'AutoRepair'],
  '@id': BUSINESS_ID,
  name: BUSINESS_NAME,
  // The domain is the brand's web address, not a second company. Declaring it
  // as an alternateName tells search engines that "fulldetailpro.com" and
  // "Mike's Pro Detail" are the same entity.
  alternateName: 'fulldetailpro.com',
  url: `${SITE_URL}/`,
  telephone: BUSINESS_PHONE,
  image: LOGO_URL,
  logo: LOGO_URL,
  serviceType: 'Mobile Car Detailing',
  description: "Mike's Pro Detail is a mobile car detailing service that comes to your home or office throughout Ventura County, California and select surrounding areas. Interior detailing, exterior detailing, full details, hybrid ceramic spray protection, headlight restoration, and engine bay cleaning.",
  priceRange: '$90-$350',
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'CA',
    addressCountry: 'US'
  },
  areaServed: [{
    '@type': 'AdministrativeArea',
    name: 'Ventura County'
  }, ...SERVICE_AREA_CITIES.map(name => ({
    '@type': 'City',
    name
  }))],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Mobile Auto Detailing Services',
    itemListElement: SERVICE_CATALOG.map(s => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: s.name,
        description: s.description,
        url: `${SITE_URL}${s.path}`,
        provider: {
          '@id': BUSINESS_ID
        }
      }
    }))
  }
};
/** The website entity, published by the business. */
export const websiteEntity = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: BUSINESS_NAME,
  alternateName: 'fulldetailpro.com',
  description: "The official website of Mike's Pro Detail, a mobile car detailing service in Ventura County, California.",
  publisher: {
    '@id': BUSINESS_ID
  },
  inLanguage: 'en-US'
};
interface HeadMetaOptions {
  /** Full <title> text, already including the brand suffix. */
  title: string;
  description: string;
  /** Path beginning with "/" — used for canonical and og:url. */
  path: string;
  ogType?: string;
  image?: string;
}
function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setLinkTag(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
/** Normalises a path into a canonical URL (https, www, no trailing slash except root). */
export function canonicalUrl(path: string): string {
  let clean = (path || '/').split('?')[0].split('#')[0];
  if (!clean.startsWith('/')) clean = `/${clean}`;
  if (clean.length > 1 && clean.endsWith('/')) clean = clean.slice(0, -1);
  return `${SITE_URL}${clean === '/' ? '/' : clean}`;
}
/**
 * Sets title, meta description, canonical, Open Graph and Twitter tags for the
 * current route. Safe to call from any page's effect.
 */
export function setHeadMeta({
  title,
  description,
  path,
  ogType = 'website',
  image = LOGO_URL
}: HeadMetaOptions) {
  const url = canonicalUrl(path);
  document.title = title;
  setMetaTag('name', 'description', description);
  setMetaTag('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1');
  setLinkTag('canonical', url);
  setMetaTag('property', 'og:type', ogType);
  setMetaTag('property', 'og:site_name', BUSINESS_NAME);
  setMetaTag('property', 'og:locale', 'en_US');
  setMetaTag('property', 'og:url', url);
  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:image', image);
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:url', url);
  setMetaTag('name', 'twitter:title', title);
  setMetaTag('name', 'twitter:description', description);
  setMetaTag('name', 'twitter:image', image);
}
/**
 * Marks a route as non-indexable (used for the 404 route and the internal
 * content export). Because this is a single-page app, the previous route's
 * canonical link stays in the DOM unless we remove it — leaving a stale
 * canonical that points a 404 at a real page. Clear it explicitly.
 */
export function setNoIndex(description?: string) {
  setMetaTag('name', 'robots', 'noindex, follow');
  if (description) setMetaTag('name', 'description', description);
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.remove();
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.remove();
}
/**
 * Injects a single JSON-LD @graph script and returns a cleanup function that
 * removes it again — keeping one structured-data block per route.
 */
export function injectJsonLd(graph: object[]): () => void {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph
  });
  document.head.appendChild(script);
  return () => {
    if (document.head.contains(script)) {
      document.head.removeChild(script);
    }
  };
}
